import {Dimension, KnownMeasure, TimeBucket} from 'app/features/analytics/features/reports/dimension';
import {DateDimension, Dimension as GraphQLDimension} from '../../../../globalTypes';
import {formatDate, formatMonth, formatQuarter, formatWeek} from 'app/features/analytics/helpers/dates';
import moment from 'moment';
import {StatsService} from 'app/features/analytics/service/stats.service';
import {Group, ReportFilters, TimeFilterValue, TimeGroup} from 'app/features/analytics/service/filter-value.interface';
import {dateStringToParts} from 'app/core/helpers/date';

export function getDefaultTimeFilter(): TimeFilterValue {
    return {
        id: DateDimension.DATE,
        from: moment().add({d: -7}).format('YYYY-MM-DD'),
        to: moment().format('YYYY-MM-DD'),
    };
}

export function isDateOrInstallDateDimension(dm) {
    return isDateDimension(dm) || isInstallDimension(dm);
}

export function isAnyTimeDimension(dm: string): boolean {
    return isTimeDimension(dm) || isRelativeTimeDimension(dm);
}

export function isRelativeTimeDimension(dm: string): boolean {
    return (GraphQLDimension.DAY_SINCE_INSTALL as any) === dm;
}

export function isDateDimension(dm: string): boolean {
    return (GraphQLDimension.DATE as any) === dm;
}

export function isInstallDimension(dm: string): boolean {
    return (GraphQLDimension.INSTALL_DATE as any) === dm;
}

export function isTimeDimension(dm: string): boolean {
    return Object.values(DateDimension).includes(dm as any);
}

export function getTimeFilter(filters: ReportFilters): TimeFilterValue {
    return filters.filter.find(f => Dimension.Time === f.id) as TimeFilterValue;
}

export function getTimeBucket(filters: ReportFilters | Group[]): TimeBucket | undefined {
    const groupByTime = (Array.isArray(filters) ? (filters as Group[]) : filters.groupBy).find(g => isTimeDimension(g.id)) as TimeGroup;
    return groupByTime ? groupByTime.timeBucket : undefined;
}

export function getDefaultTimeBucket(timeFilter: TimeFilterValue): TimeBucket {
    const from = moment(timeFilter.from);
    const to = moment(timeFilter.to);
    const MinMonthDays = 32;
    if (to.diff(from, 'day') < MinMonthDays) {
        return TimeBucket.DAY;
    }

    if (to.diff(from, 'week') < 9) {
        return TimeBucket.WEEK;
    }

    return TimeBucket.MONTH;
}

export const dateFormatters = {
    [TimeBucket.DAY]: value => formatDate(value),
    [TimeBucket.WEEK]: value => formatWeek(value),
    [TimeBucket.MONTH]: value => formatMonth(value),
    [TimeBucket.QUARTER]: value => formatQuarter(value),
};

export function requestTypeLabel(filterValue: ReportFilters): string {
    if (
        filterValue.groupBy.some(elem => elem.id === Dimension.LineItem) ||
        filterValue.filter.some(elem => elem.id === Dimension.LineItem && elem.value.length)
    ) {
        return 'analytics.measures.lineItemRequests';
    }
    if (
        filterValue.groupBy.some(elem => elem.id === Dimension.AdNetwork) ||
        filterValue.filter.some(elem => elem.id === Dimension.AdNetwork && elem.value.length)
    ) {
        return 'analytics.measures.networkRequests';
    }
    return 'analytics.measures.waterfallRequests';
}

export function fillRateTypeLabel(filterValue: ReportFilters): string {
    if (
        filterValue.groupBy.some(elem => elem.id === Dimension.LineItem) ||
        filterValue.filter.some(elem => elem.id === Dimension.LineItem && elem.value.length)
    ) {
        return 'analytics.measures.lineItemFillRate';
    }
    if (
        filterValue.groupBy.some(elem => elem.id === Dimension.AdNetwork) ||
        filterValue.filter.some(elem => elem.id === Dimension.AdNetwork && elem.value.length)
    ) {
        return 'analytics.measures.networkFillRate';
    }

    if (
        filterValue.groupBy.some(elem => elem.id === Dimension.post_bid) ||
        filterValue.filter.some(elem => elem.id === Dimension.post_bid && elem.value.length)
    ) {
        return 'analytics.measures.competitorFactor';
    }

    return 'analytics.measures.waterfallFillRate';
}

export function formatDates(rows: string[][], timeHeaderIndex, timeBucket) {
    if (!timeBucket) {
        return;
    }

    const dateFormatter = dateFormatters[timeBucket];
    rows.forEach(row => (row[timeHeaderIndex] = setValue(row[timeHeaderIndex], dateFormatter(getValue(row[timeHeaderIndex])))));
}

export function getValue(rowCell: string | {value: string}, valueProp: string = 'value'): string {
    const value = rowCell[valueProp] === undefined ? rowCell : rowCell[valueProp];
    return value === '-' ? '0' : value;
}

export function extractDate(inputStringDate: string): number {
    const parts = dateStringToParts(inputStringDate);
    if (parts) {
        return Date.UTC(parts.year, parts.month - 1, parts.date);
    }
    return;
}

function setValue<T extends string | {value: string}>(cell: T, value: string): T {
    if (typeof cell === 'string') {
        // @ts-ignore
        return value;
    }
    return {
        // @ts-ignore
        ...cell,
        value,
    };
}

export function getValueWithInt(rowCell: string | {value: string}, valueProp: string = 'value', toInt?: boolean): number | string {
    if (!toInt) {
        return getValue(rowCell, valueProp);
    }
    const value = rowCell[valueProp] === undefined ? rowCell : rowCell[valueProp];
    return value === '-' ? 0 : parseInt(value, 10);
}

function compareRowValues(a, b, toInt): number {
    return getValueWithInt(a, 'id', toInt) > getValueWithInt(b, 'id', toInt) ? 1 : -1;
}

export function sort2Times(rows: any[], timeHeaderIndex: number, timeIndex2: number, toInt1?: boolean, toInt2?: boolean): any[] {
    if (timeHeaderIndex !== -1) {
        // sort by date
        rows.sort((a, b) => {
            if (getValue(a[timeHeaderIndex], 'id') === getValue(b[timeHeaderIndex], 'id')) {
                if (timeIndex2 === -1) {
                    return 0;
                }
                return compareRowValues(a[timeIndex2], b[timeIndex2], toInt2);
            }
            return compareRowValues(a[timeHeaderIndex], b[timeHeaderIndex], toInt1);
        });
        // sorted
    }
    return rows;
}

export function sortRowsByDate(rows: any[], timeHeaderIndex: number, timeBucket: TimeBucket): any[] {
    if (timeHeaderIndex !== -1) {
        // sort by date
        rows.sort((a, b) => {
            if (getValue(a[timeHeaderIndex], 'id') === getValue(b[timeHeaderIndex], 'id')) {
                return 0;
            }
            return getValue(a[timeHeaderIndex], 'id') > getValue(b[timeHeaderIndex], 'id') ? 1 : -1;
        });
        // sorted

        // format dates
        formatDates(rows, timeHeaderIndex, timeBucket);
    }
    return rows;
}

export function getMeasureLabel(measure: KnownMeasure, timeBucket: TimeBucket): string {
    if (measure === KnownMeasure.Arpu) {
        return getArpuByTimeBucket(timeBucket);
    }

    if (measure === KnownMeasure.ActiveUsersPerAd) {
        return getDauByTimeBucket(timeBucket);
    }

    // return measureLabels[measure];
}

export function getArpuByTimeBucket(timeBucket: TimeBucket): string {
    switch (timeBucket) {
        case TimeBucket.DAY:
            return 'analytics.measures.arpdau';
        case TimeBucket.WEEK:
            return 'analytics.measures.arpwau';
        case TimeBucket.MONTH:
            return 'analytics.measures.arpmau';
        default:
            return 'analytics.measures.arpu';
    }
}

export function getDauByTimeBucket(timeBucket: TimeBucket): string {
    switch (timeBucket) {
        case TimeBucket.DAY:
            return 'analytics.measures.dau';
        case TimeBucket.WEEK:
            return 'analytics.measures.wau';
        case TimeBucket.MONTH:
            return 'analytics.measures.mau';
        default:
            return 'analytics.measures.dau';
    }
}

export function isFiltersChanged(prevFilter: ReportFilters, currFilter: ReportFilters, statsService: StatsService): boolean {
    if (!prevFilter && !currFilter) {
        return false;
    }
    if (!prevFilter && currFilter) {
        return true;
    }
    return !statsService.compareFilters(prevFilter, currFilter);
}

const MsInDay = 86400000;
const MsInWeek = 604800000; // MsInDay * 7;
const MsInMonth = 2419200000; // MsInWeek * 4;
const MsInQuarter = 7257600000; // MsInMonth * 3

export function getMsInBucket(timeBucket: TimeBucket): number {
    switch (timeBucket) {
        case TimeBucket.DAY:
            return MsInDay;
        case TimeBucket.WEEK:
            return MsInWeek;
        case TimeBucket.MONTH:
            return MsInMonth;
        case TimeBucket.QUARTER:
            return MsInQuarter;
    }
}
